import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import {
  BriefcaseIcon,
  CopyIcon,
  CopyPlus,
  EditIcon,
  RefreshCcwIcon,
  Trash2Icon,
  UploadIcon,
  WrenchIcon,
} from 'lucide-react'

import {
  ActionsMenu,
  Button,
  DropdownMenuItem,
  DropdownMenuSeparator,
  StatusBadge,
  statusBadgeVariants,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  allowsManyProjects,
  isTrialPlan,
  useAccount,
  useIsAdmin,
} from '@/modules/account'
import { hasAnalysisGenerated, useAnalysis } from '@/modules/analysis'
import { duplicatesPath } from '@/modules/insights/utils'
import {
  CaseEditButton,
  DeleteProjectsDialog,
  EditProjectDialog,
  type Project,
  useInsights,
  useProjectActions,
  useProjectsMutations,
} from '@/modules/projects'
import { useDialogActions } from '@/stores/dialog'
import { getProjectPath } from '@/utils'
import { isProjectDeletable } from '../../expiration'
import {
  hasInsightsResult,
  isInsightsProcessing,
  isProjectBusy,
  isProjectProcessing,
} from '../../status'

interface HeaderActionsProps {
  project: Project
  isWizardVisible: boolean
}

export default function HeaderActions({
  project,
  isWizardVisible,
}: HeaderActionsProps) {
  const { openDialog } = useDialogActions()
  const navigate = useNavigate()
  const insights = useInsights()
  const location = useLocation()
  const analysis = useAnalysis()
  const account = useAccount()
  const isAdmin = useIsAdmin()
  const { duplicateProject, unarchiveProjects } = useProjectsMutations()
  const { updateProject } = useProjectActions()
  const { successToast, errorToast } = useToast()

  const duplicate = () => {
    duplicateProject(project.slug)
      .then(({ slug }) => navigate({ to: getProjectPath({ slug }) }))
      .catch((error) =>
        errorToast("We couldn't duplicate this project", error as Error),
      )
  }

  const unarchive = () => {
    unarchiveProjects([project])
      .then(() => {
        updateProject({ archivedAt: null })
        successToast('Project unarchived')
      })
      .catch((error) =>
        errorToast("We couldn't unarchive this project", error as Error),
      )
  }

  const showInsightsActions =
    insights &&
    hasInsightsResult(insights) &&
    !isInsightsProcessing(insights) &&
    location.pathname.includes('/insights')

  const showAnalysisActions =
    analysis &&
    hasAnalysisGenerated(analysis) &&
    location.pathname.includes('/analysis')

  return (
    <div className="flex items-center gap-2">
      {project.archivedAt && (
        <>
          <StatusBadge slots={statusBadgeVariants()} label="Archived" />
          <Button size="sm" theme="outline" onClick={unarchive}>
            Unarchive
          </Button>
        </>
      )}
      <ActionsMenu>
        <DropdownMenuItem
          onClick={() => openDialog(EditProjectDialog.id, project)}
        >
          <EditIcon className="h-4 w-4" />
          Edit project
        </DropdownMenuItem>
        {!isWizardVisible && (
          <DropdownMenuItem>
            <CaseEditButton className="gap-2 flex items-center">
              <BriefcaseIcon className="h-4 w-4" />
              Edit case details
            </CaseEditButton>
          </DropdownMenuItem>
        )}
        {allowsManyProjects(account?.planKind) && (
          <DropdownMenuItem
            onClick={duplicate}
            disabled={isProjectBusy(project)}
          >
            <CopyPlus className="h-4 w-4" />
            Duplicate project
          </DropdownMenuItem>
        )}
        {!isWizardVisible && (
          <DropdownMenuItem asChild>
            <Link to={`${getProjectPath({ slug: project.slug })}/files`}>
              <UploadIcon className="h-4 w-4" />
              Upload statements
            </Link>
          </DropdownMenuItem>
        )}
        {!isTrialPlan(account?.planKind) && (
          <DropdownMenuItem asChild>
            <Link to={duplicatesPath(project.slug)}>
              <CopyIcon className="h-4 w-4" />
              Possible duplicates
            </Link>
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link to={`${getProjectPath({ slug: project.slug })}/workbench`}>
              <WrenchIcon className="h-4 w-4" />
              Workbench
              <span className="ml-auto rounded-full bg-red-600 px-1.5 py-0.5 font-semibold text-[9px] text-white uppercase tracking-wider">
                Admin
              </span>
            </Link>
          </DropdownMenuItem>
        )}
        {isProjectDeletable(project, account?.planKind) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => openDialog(DeleteProjectsDialog.id, [project])}
              destructive
              disabled={isProjectBusy(project)}
            >
              <Trash2Icon className="h-4 w-4" />
              Delete project
            </DropdownMenuItem>
          </>
        )}
      </ActionsMenu>

      {showInsightsActions && !isProjectProcessing(project) && (
        <Button size="sm" onClick={() => openDialog('regenerate-insights')}>
          <RefreshCcwIcon className="h-4 w-4" />
          Regenerate Insights
        </Button>
      )}
      {showAnalysisActions && !account?.isAnalysisBlocked && (
        <Button size="sm" onClick={() => openDialog('delete-analysis')}>
          <RefreshCcwIcon className="h-4 w-4" />
          Regenerate Report
        </Button>
      )}
    </div>
  )
}
